// SPDX-License-Identifier: AGPL-3.0-or-later

import styles from '@app/features/app/components/dialogs/shared/SettingsIconChoiceCards.module.css';
import {CheckIcon} from '@app/features/ui/icons/Icons';
import {clsx} from 'clsx';
import type React from 'react';
import {useRef} from 'react';

export interface SettingsIconChoiceCardOption<T extends string | number> {
	value: T;
	name: string;
	icon: React.ReactNode;
}

interface SettingsIconChoiceCardsProps<T extends string | number> {
	options: ReadonlyArray<SettingsIconChoiceCardOption<T>>;
	value: T;
	onChange: (value: T) => void;
	'aria-label': string;
	/** One line under the cards explaining the current choice. */
	note?: React.ReactNode;
	disabled?: boolean;
	className?: string;
	'data-flx'?: string;
}

/**
 * KONVO (notifications reskin 2026-09): a short one-of-N choice as small cards with an icon
 * tile and a name, the picked one ringed with a check (like the Appearance theme cards).
 * Arrow keys move the choice like a radio group.
 */
export function SettingsIconChoiceCards<T extends string | number>({
	options,
	value,
	onChange,
	'aria-label': ariaLabel,
	note,
	disabled,
	className,
	'data-flx': dataFlx,
}: SettingsIconChoiceCardsProps<T>): React.ReactElement {
	const refs = useRef<Array<HTMLButtonElement | null>>([]);
	const handleKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>, index: number) => {
		const step =
			event.key === 'ArrowRight' || event.key === 'ArrowDown'
				? 1
				: event.key === 'ArrowLeft' || event.key === 'ArrowUp'
					? -1
					: 0;
		if (step === 0 || disabled) return;
		event.preventDefault();
		const next = (index + step + options.length) % options.length;
		onChange(options[next].value);
		refs.current[next]?.focus();
	};
	return (
		<div className={clsx(styles.wrap, className)} data-flx={dataFlx}>
			<div role="radiogroup" aria-label={ariaLabel} className={styles.grid}>
				{options.map((option, index) => {
					const checked = option.value === value;
					return (
						<button
							key={String(option.value)}
							ref={(node) => {
								refs.current[index] = node;
							}}
							type="button"
							role="radio"
							aria-checked={checked}
							tabIndex={checked ? 0 : -1}
							disabled={disabled}
							className={clsx(styles.card, checked && styles.checked)}
							onClick={() => onChange(option.value)}
							onKeyDown={(event) => handleKeyDown(event, index)}
							data-flx={dataFlx ? `${dataFlx}.${String(option.value)}` : undefined}
						>
							{checked && (
								<span className={styles.check} aria-hidden="true">
									<CheckIcon size={13} weight="bold" />
								</span>
							)}
							<span className={styles.icon} aria-hidden="true">
								{option.icon}
							</span>
							<span className={styles.name}>{option.name}</span>
						</button>
					);
				})}
			</div>
			{note ? <p className={styles.note}>{note}</p> : null}
		</div>
	);
}
